import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { knowledgeBaseAPI, subscriptionAPI } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft, Plus, BookOpen, ThumbsUp, Loader2, Crown, Search,
  ChevronDown, ChevronUp, Trash2, Filter,
} from "lucide-react";

const categoryColor = (c) => ({
  Recovery: "bg-red-500/15 text-red-600 border-red-500/30",
  Maintenance: "bg-blue-500/15 text-blue-600 border-blue-500/30",
  "Trail Tips": "bg-green-500/15 text-green-600 border-green-500/30",
  "Tires & Wheels": "bg-amber-500/15 text-amber-600 border-amber-500/30",
  Drivetrain: "bg-purple-500/15 text-purple-600 border-purple-500/30",
  Suspension: "bg-cyan-500/15 text-cyan-600 border-cyan-500/30",
  Electrical: "bg-yellow-500/15 text-yellow-700 border-yellow-500/30",
  Overlanding: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
}[c] || "bg-zinc-500/15 text-zinc-600 border-zinc-500/30");

export default function KnowledgeBase() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "General" });

  useEffect(() => { loadData(); }, [selectedCategory]);

  const loadData = async () => {
    try {
      const [articlesRes, subRes] = await Promise.all([
        knowledgeBaseAPI.list(selectedCategory),
        subscriptionAPI.status(),
      ]);
      setArticles(articlesRes.data.articles || []);
      setCategories(articlesRes.data.categories || []);
      setIsPro(subRes.data.is_pro);
    } catch {
      toast.error("Failed to load knowledge base");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setCreating(true);
    try {
      await knowledgeBaseAPI.create(form);
      toast.success("Article published!");
      setShowCreate(false);
      setForm({ title: "", content: "", category: "General" });
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const handleUpvote = async (id) => {
    try {
      await knowledgeBaseAPI.upvote(id);
      await loadData();
    } catch {
      toast.error("Failed to upvote");
    }
  };

  const handleDelete = async (id) => {
    try {
      await knowledgeBaseAPI.delete(id);
      toast.success("Article deleted");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const userId = JSON.parse(localStorage.getItem("user") || "{}").id;

  const filtered = articles.filter((a) =>
    !searchQuery || a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                Knowledge Base
              </h1>
              <p className="text-xs text-muted-foreground">{filtered.length} articles</p>
            </div>
          </div>
          {isPro ? (
            <Button onClick={() => setShowCreate(true)} data-testid="create-article-btn">
              <Plus className="w-4 h-4 mr-2" /> Write Article
            </Button>
          ) : (
            <Button variant="outline" onClick={() => navigate("/subscription")} data-testid="upgrade-btn">
              <Crown className="w-4 h-4 mr-2" /> Pro to Write
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Search + Filter */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles..."
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              size="sm"
              onClick={() => { setSelectedCategory(null); setLoading(true); }}
              data-testid="filter-all"
            >
              All
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? "default" : "outline"}
                size="sm"
                onClick={() => { setSelectedCategory(cat); setLoading(true); }}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>

        {/* Articles */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">No articles found</p>
          </div>
        ) : (
          filtered.map((article) => {
            const isExpanded = expandedArticle === article.id;
            return (
              <Card key={article.id} data-testid={`article-${article.id}`}>
                <CardContent className="pt-4">
                  <div
                    className="cursor-pointer"
                    onClick={() => setExpandedArticle(isExpanded ? null : article.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-base">{article.title}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={`text-xs ${categoryColor(article.category)}`}>
                            {article.category}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            by {article.author}
                          </span>
                          {article.is_seed && (
                            <Badge variant="outline" className="text-xs">Official</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="prose prose-sm prose-emerald max-w-none [&_h1]:text-base [&_h1]:font-bold [&_h2]:text-sm [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-semibold [&_p]:my-1.5 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_strong]:font-bold [&_hr]:my-3 [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded">
                        <ReactMarkdown>{article.content}</ReactMarkdown>
                      </div>
                      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleUpvote(article.id); }}
                          className="text-muted-foreground hover:text-primary"
                          data-testid={`upvote-${article.id}`}
                        >
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          {article.upvotes || 0}
                        </Button>
                        {article.user_id === userId && !article.is_seed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDelete(article.id); }}
                            className="text-muted-foreground hover:text-destructive ml-auto"
                            data-testid={`delete-article-${article.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </main>

      {/* Create Article Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Share Your Knowledge</DialogTitle>
            <DialogDescription>Write a tip or guide for the off-road community.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., How to Properly Break In New Lockers"
                data-testid="article-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="article-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Content * (Markdown supported)</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Share your experience, tips, or guides..."
                rows={8}
                data-testid="article-content-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="publish-article-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
